/**
 * ChangeControlDashboard — CC KPIs (CC-14, URS / Design Table 13).
 * Open/Closed/Overdue, avg closure days, by type/dept/risk/status,
 * "implemented before approval = 0" compliance signal, top-10 overdue, monthly
 * trend, and a rules-based insight panel. Route: /change-control/dashboard.
 * Bilingual (en-US / ar-SA).
 */
import { Link, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { PageHero } from '@edms/design-system';
import styles from '../Recalls/Recalls.module.css';
import { useCcDashboard, CC_STATUS_LABEL } from './useChangeControl';

function Bars({ rows, color }: { rows: Array<{ label: string; n: number }>; color: string }) {
  const max = Math.max(1, ...rows.map((r) => r.n));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {rows.map((r, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
          <span style={{ flex: '0 0 130px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.label}</span>
          <div style={{ flex: 1, background: 'var(--color-surface-subtle, #F2F4F7)', borderRadius: 6, height: 16, position: 'relative' }}>
            <div style={{ width: `${(r.n / max) * 100}%`, background: color, height: 16, borderRadius: 6, minWidth: r.n > 0 ? 4 : 0 }} />
          </div>
          <span style={{ flex: '0 0 28px', textAlign: 'end', fontWeight: 700 }}>{r.n}</span>
        </div>
      ))}
    </div>
  );
}

export function ChangeControlDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const q = useCcDashboard();
  const td = (k: string, opts?: Record<string, unknown>) => t(`changeControl.dashboard.${k}`, opts ?? {});
  const tStatus = (s: string) => t(`changeControl.status.${s}`, { defaultValue: CC_STATUS_LABEL[s] ?? s });
  const tType = (ct: string) => (ct === 'UNSPECIFIED' ? '—' : t(`changeControl.changeType.${ct}`, { defaultValue: ct }));
  const tDept = (d: string) => t(`changeControl.dept.${d}`, { defaultValue: d });
  const tRisk = (g: string) => (g === 'GMP' ? t('changeControl.register.gmpYes') : g === 'NON_GMP' ? t('changeControl.register.gmpNo') : g);

  return (
    <main className={styles.page}>
      <nav className={styles.crumbs} aria-label="Breadcrumb">
        <Link to="/change-control" className={styles.crumb}>{t('changeControl.register.title')}</Link>
        <span className={styles.crumbSep} aria-hidden>/</span>
        <span>{td('title')}</span>
      </nav>
      <PageHero eyebrow={td('eyebrow')} title={td('title')} subtitle={td('subtitle')} />

      {q.isLoading ? <div className={styles.empty}>{td('loading')}</div>
        : q.isError || !q.data ? <div className={styles.empty}>{td('loadError')}</div>
          : (() => {
            const d = q.data;
            const tot = d.totals;
            return (
              <>
                <div className={styles.kpiRow}>
                  <div className={styles.kpi}><span className={styles.kpiNum}>{tot.total}</span><span className={styles.kpiLabel}>{td('kpiTotal')}</span></div>
                  <div className={styles.kpi}><span className={styles.kpiNum}>{tot.open}</span><span className={styles.kpiLabel}>{td('kpiOpen')}</span></div>
                  <div className={styles.kpi}><span className={styles.kpiNum}>{tot.closed}</span><span className={styles.kpiLabel}>{td('kpiClosed')}</span></div>
                  <div className={`${styles.kpi} ${tot.overdue > 0 ? styles.kpiAlert : ''}`}><span className={styles.kpiNum}>{tot.overdue}</span><span className={styles.kpiLabel}>{td('kpiOverdue')}</span></div>
                  <div className={styles.kpi}><span className={styles.kpiNum}>{tot.avg_closure_days}</span><span className={styles.kpiLabel}>{td('kpiAvgClosure')}</span></div>
                  <div className={`${styles.kpi} ${tot.implemented_before_approval > 0 ? styles.kpiAlert : ''}`}><span className={styles.kpiNum}>{tot.implemented_before_approval}</span><span className={styles.kpiLabel}>{td('kpiBeforeApproval')}</span></div>
                </div>

                <section className={styles.panel}>
                  <h2 className={styles.panelTitle}>{td('insights')}</h2>
                  <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {d.insights.map((ins, i) => (
                      <li key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
                        <span style={{ width: 10, height: 10, borderRadius: 999, background: ins.kind === 'ok' ? '#12B76A' : ins.kind === 'warn' ? '#F04438' : '#1570EF' }} />
                        {t(`changeControl.ccInsight.${ins.key}`, { ...ins.params, defaultValue: ins.key })}
                      </li>
                    ))}
                  </ul>
                </section>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
                  <section className={styles.panel}>
                    <h2 className={styles.panelTitle}>{td('byStatus')}</h2>
                    <Bars rows={d.by_status.map((r) => ({ label: tStatus(r.status), n: r.n }))} color="#1570EF" />
                  </section>
                  <section className={styles.panel}>
                    <h2 className={styles.panelTitle}>{td('byType')}</h2>
                    <Bars rows={d.by_type.map((r) => ({ label: tType(r.change_type), n: r.n }))} color="#7A5AF8" />
                  </section>
                  <section className={styles.panel}>
                    <h2 className={styles.panelTitle}>{td('byDept')}</h2>
                    <Bars rows={d.by_department.map((r) => ({ label: tDept(r.department), n: r.n }))} color="#0BA5EC" />
                  </section>
                  <section className={styles.panel}>
                    <h2 className={styles.panelTitle}>{td('byRisk')}</h2>
                    <Bars rows={d.by_risk.map((r) => ({ label: tRisk(r.gmp), n: r.n }))} color="#F79009" />
                  </section>
                </div>

                <section className={styles.panel}>
                  <h2 className={styles.panelTitle}>{td('topOverdue')}</h2>
                  {d.top_overdue.length === 0 ? <span className={styles.terminal}>{td('noOverdue')}</span> : (
                    <table className={styles.table}>
                      <thead><tr><th>{td('thCr')}</th><th>{td('thTitle')}</th><th>{td('thOpenActions')}</th><th>{td('thDaysOverdue')}</th></tr></thead>
                      <tbody>
                        {d.top_overdue.map((r) => (
                          <tr key={r.cr_id} className={styles.row} onClick={() => navigate({ to: '/change-control/$id', params: { id: r.cr_id } })}>
                            <td className={styles.mono}>{r.cr_number ?? '—'}</td>
                            <td>{r.title}</td>
                            <td>{r.open_overdue}</td>
                            <td><span className={`${styles.pill} ${styles.cls1}`}>{r.days_overdue}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </section>

                {d.trend.length > 0 && (
                  <section className={styles.panel}>
                    <h2 className={styles.panelTitle}>{td('trend')}</h2>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {d.trend.map((m) => {
                        const max = Math.max(1, ...d.trend.map((x) => Math.max(x.created, x.closed)));
                        return (
                          <div key={m.ym} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                            <span style={{ flex: '0 0 70px', fontWeight: 600 }}>{m.ym}</span>
                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <div style={{ width: `${(m.created / max) * 100}%`, background: '#1570EF', height: 10, borderRadius: 4, minWidth: m.created > 0 ? 4 : 0 }} />
                                <span style={{ fontSize: 11, color: 'var(--color-text-tertiary, #6B7989)' }}>{td('created')} {m.created}</span>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <div style={{ width: `${(m.closed / max) * 100}%`, background: '#12B76A', height: 10, borderRadius: 4, minWidth: m.closed > 0 ? 4 : 0 }} />
                                <span style={{ fontSize: 11, color: 'var(--color-text-tertiary, #6B7989)' }}>{td('closedLbl')} {m.closed}</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </section>
                )}
              </>
            );
          })()}
    </main>
  );
}
